import { Config } from '@/lib/config';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const navigate = useNavigate();

  const onRedirectCallback = React.useCallback(
    (appState?: { returnTo?: string }) => {
      // Navigate to the returnTo path or default to current location
      navigate(appState?.returnTo || window.location.pathname, {
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <Auth0Provider
      domain={Config.auth0Domain!}
      clientId={Config.auth0ClientId!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: Config.auth0Audience,
        scope: 'openid profile email offline_access',
      }}
      useRefreshTokens
      useRefreshTokensFallback={false}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

export const useAuth = useAuth0;

export const useAccessToken = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const getToken = React.useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: Config.auth0Audience,
        },
      });
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  return getToken;
};
